/**
 * Shared domain types — imported by BOTH the Worker and the web app.
 * No runtime dependencies. DTO field names are camelCase (DB columns are snake_case).
 *
 * These types are the single source of truth for entity shapes. `api.ts` builds the
 * request/response contracts on top of them.
 */

/* ------------------------------------------------------------------ */
/* Enums / unions                                                      */
/* ------------------------------------------------------------------ */

export const PLATFORM_IDS = [
  'facebook',
  'instagram',
  'x',
  'linkedin',
  'wordpress',
  'tiktok',
  'youtube',
] as const;

export type PlatformId = (typeof PLATFORM_IDS)[number];

/** Platforms that can actually publish at launch (tiktok/youtube are "coming soon"). */
export const LIVE_PLATFORM_IDS = [
  'facebook',
  'instagram',
  'x',
  'linkedin',
  'wordpress',
] as const satisfies readonly PlatformId[];

export type LivePlatformId = (typeof LIVE_PLATFORM_IDS)[number];

export const WORKSPACE_ROLES = ['owner', 'admin', 'member'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const POST_STATUSES = [
  'draft',
  'scheduled',
  'publishing',
  'published',
  'partial',
  'failed',
  'archived',
] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const PLATFORM_POST_STATUSES = [
  'draft',
  'approved',
  'scheduled',
  'publishing',
  'published',
  'failed',
  'skipped',
] as const;
export type PlatformPostStatus = (typeof PLATFORM_POST_STATUSES)[number];

export const SOCIAL_ACCOUNT_STATUSES = [
  'active',
  'expired',
  'error',
  'revoked',
  'simulated',
] as const;
export type SocialAccountStatus = (typeof SOCIAL_ACCOUNT_STATUSES)[number];

export type MediaKind = 'image' | 'video';
export type MediaSource = 'upload' | 'ai_generated';
export type WritingSampleSource = 'manual' | 'import' | 'edit_learned';

export const AI_GENERATION_KINDS = [
  'draft',
  'image',
  'improve',
  'style_analysis',
  'ideas',
  'hashtags',
  'alt_text',
] as const;
export type AiGenerationKind = (typeof AI_GENERATION_KINDS)[number];

export type AiGenerationStatus = 'ok' | 'error';

/** Which Gemini backend produced a generation. `mock` = no credentials configured. */
export type AiProvider = 'vertex' | 'gemini-api' | 'mock';

export type NotificationType =
  | 'publish_success'
  | 'publish_failure'
  | 'post_scheduled'
  | 'token_expiring'
  | 'token_expired'
  | 'invite_sent'
  | 'invite_accepted'
  | 'member_joined'
  | 'style_analyzed'
  | 'system';

export type ActivityAction =
  | 'workspace.created'
  | 'workspace.updated'
  | 'member.invited'
  | 'member.joined'
  | 'member.role_changed'
  | 'member.removed'
  | 'business_profile.updated'
  | 'style.sample_added'
  | 'style.sample_deleted'
  | 'style.analyzed'
  | 'account.connected'
  | 'account.disconnected'
  | 'media.uploaded'
  | 'media.deleted'
  | 'post.created'
  | 'post.updated'
  | 'post.deleted'
  | 'post.generated'
  | 'post.scheduled'
  | 'post.published'
  | 'post.failed'
  | 'ai.image_generated'
  | 'dev.seeded';

export type ThemePreference = 'dark' | 'light' | 'system';

/* ------------------------------------------------------------------ */
/* Entities                                                            */
/* ------------------------------------------------------------------ */

export interface User {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  googleId: string | null;
  isSuperadmin: boolean;
  createdAt: number;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  /** IANA timezone, e.g. "America/Chicago". */
  timezone: string;
  logoMediaId: string | null;
  createdBy: string | null;
  createdAt: number;
}

/** A workspace plus the current user's relationship to it. */
export interface WorkspaceSummary extends Workspace {
  role: WorkspaceRole;
  memberCount: number;
}

export interface Membership {
  id: string;
  workspaceId: string;
  userId: string;
  role: WorkspaceRole;
  createdAt: number;
}

export interface MemberWithUser extends Membership {
  user: Pick<User, 'id' | 'email' | 'name' | 'avatarUrl'>;
}

export interface Invite {
  id: string;
  email: string;
  workspaceId: string;
  role: WorkspaceRole;
  token: string;
  invitedBy: string | null;
  expiresAt: number;
  acceptedAt: number | null;
  createdAt: number;
}

/** Safe subset shown on the public /accept-invite page (no token echo needed). */
export interface InvitePreview {
  email: string;
  role: WorkspaceRole;
  workspaceName: string;
  invitedByName: string | null;
  expiresAt: number;
  expired: boolean;
  accepted: boolean;
}

export interface BusinessLink {
  label: string;
  url: string;
}

export interface BusinessProfile {
  id: string;
  workspaceId: string;
  description: string | null;
  industry: string | null;
  audience: string | null;
  toneDefault: string | null;
  productsServices: string | null;
  website: string | null;
  phone: string | null;
  address: string | null;
  defaultHashtags: string[];
  links: BusinessLink[];
  extraContext: string | null;
  updatedAt: number;
}

/** Writable fields of a business profile (PUT body / form state). */
export type BusinessProfileInput = Partial<
  Omit<BusinessProfile, 'id' | 'workspaceId' | 'updatedAt'>
>;

export interface WritingSample {
  id: string;
  workspaceId: string;
  userId: string | null;
  title: string | null;
  content: string;
  source: WritingSampleSource;
  platform: PlatformId | null;
  createdAt: number;
}

export interface StyleAttributes {
  tone: string;
  formality: string;
  sentenceStyle: string;
  vocabulary: string;
  emojiUsage: string;
  humor: string;
  punctuationQuirks: string;
  ctaStyle: string;
  do: string[];
  dont: string[];
}

export interface StyleProfile {
  id: string;
  workspaceId: string;
  /** null = the workspace-wide default profile. */
  userId: string | null;
  name: string;
  summary: string | null;
  attributes: StyleAttributes;
  exemplars: string[];
  version: number;
  updatedAt: number;
}

/**
 * Connector-specific account metadata. Kept as a single open-ish shape so all
 * connectors can share one column without a discriminated union everywhere.
 */
export interface SocialAccountMetadata {
  /** Facebook: the Page id we publish to. */
  pageId?: string;
  pageName?: string;
  /** Instagram: the IG Business user id linked to the page. */
  igUserId?: string;
  username?: string;
  /** WordPress: site root, e.g. https://example.com */
  siteUrl?: string;
  wpUsername?: string;
  /** LinkedIn: urn:li:person:xxx or urn:li:organization:xxx */
  authorUrn?: string;
  /** X: numeric user id + handle. */
  xUserId?: string;
  /** Free-form extras a connector wants to remember. */
  [key: string]: string | number | boolean | undefined;
}

/** Social account as exposed by the API — never includes tokens. */
export interface SocialAccount {
  id: string;
  workspaceId: string;
  platform: PlatformId;
  displayName: string;
  externalId: string | null;
  avatarUrl: string | null;
  tokenExpiresAt: number | null;
  scopes: string[];
  metadata: SocialAccountMetadata;
  status: SocialAccountStatus;
  connectedBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface MediaAsset {
  id: string;
  workspaceId: string;
  kind: MediaKind;
  r2Key: string;
  filename: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  durationS: number | null;
  altText: string | null;
  source: MediaSource;
  aiPrompt: string | null;
  createdBy: string | null;
  createdAt: number;
  /** Convenience: app-relative URL for `GET /api/media/file/:key+`. */
  url: string;
}

export interface MediaRendition {
  id: string;
  assetId: string;
  platform: PlatformId;
  /** e.g. "square", "portrait-4x5", "landscape-16x9" */
  label: string;
  r2Key: string;
  width: number;
  height: number;
  createdAt: number;
  url: string;
}

export interface PostMediaRef {
  mediaId: string;
  sortOrder: number;
}

/** Media selected for one platform variant (optionally a platform rendition). */
export interface PlatformPostMedia {
  assetId: string;
  renditionId?: string;
}

/**
 * Per-platform extras. One flat shape shared by all platforms:
 *  - WordPress: title, slug, excerpt, categories, tags, status
 *  - Instagram: firstComment
 *  - X: replyThread (additional tweets posted as replies)
 *  - Facebook/LinkedIn: linkUrl
 */
export interface PlatformPostMeta {
  title?: string;
  slug?: string;
  excerpt?: string;
  categories?: string[];
  tags?: string[];
  firstComment?: string;
  replyThread?: string[];
  linkUrl?: string;
  /** Set by AI drafting when it suggests an image for this variant. */
  imagePrompt?: string;
  /** Hashtags kept separately from the body so the UI can chip-edit them. */
  hashtags?: string[];
}

export interface Post {
  id: string;
  workspaceId: string;
  title: string | null;
  rawContent: string;
  status: PostStatus;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface PlatformPost {
  id: string;
  postId: string;
  workspaceId: string;
  platform: PlatformId;
  socialAccountId: string | null;
  content: string;
  media: PlatformPostMedia[];
  status: PlatformPostStatus;
  /** Unix ms, UTC. Rendered in the workspace timezone. */
  scheduledAt: number | null;
  publishedAt: number | null;
  externalId: string | null;
  externalUrl: string | null;
  error: string | null;
  retryCount: number;
  meta: PlatformPostMeta;
  simulated: boolean;
  updatedAt: number;
}

/** A post with everything the composer / detail drawer needs. */
export interface PostDetail extends Post {
  platformPosts: PlatformPost[];
  media: MediaAsset[];
  createdByUser: Pick<User, 'id' | 'name' | 'email' | 'avatarUrl'> | null;
}

/** Compact row for the posts list. */
export interface PostListItem extends Post {
  platforms: Array<{
    id: string;
    platform: PlatformId;
    status: PlatformPostStatus;
    scheduledAt: number | null;
    publishedAt: number | null;
    externalUrl: string | null;
    simulated: boolean;
  }>;
  mediaCount: number;
  excerpt: string;
}

export interface AiGeneration {
  id: string;
  workspaceId: string | null;
  userId: string | null;
  kind: AiGenerationKind;
  model: string;
  provider: AiProvider;
  promptSummary: string | null;
  status: AiGenerationStatus;
  error: string | null;
  durationMs: number | null;
  createdAt: number;
}

export interface Notification {
  id: string;
  workspaceId: string;
  /** null = broadcast to every member of the workspace. */
  userId: string | null;
  type: NotificationType;
  title: string;
  body: string | null;
  link: string | null;
  readAt: number | null;
  createdAt: number;
}

export interface ActivityLogEntry {
  id: string;
  workspaceId: string;
  userId: string | null;
  action: ActivityAction | string;
  targetType: string | null;
  targetId: string | null;
  meta: Record<string, unknown>;
  createdAt: number;
  user?: Pick<User, 'id' | 'name' | 'email' | 'avatarUrl'> | null;
}

/* ------------------------------------------------------------------ */
/* Cross-cutting value types                                           */
/* ------------------------------------------------------------------ */

/** Result of validating content against a platform spec. */
export interface ValidationIssue {
  level: 'error' | 'warning';
  code:
    | 'too_long'
    | 'too_many_hashtags'
    | 'too_many_images'
    /** Within the platform's limit, but more than our connector publishes today. */
    | 'extra_images_dropped'
    | 'media_required'
    | 'media_unsupported'
    | 'title_required'
    | 'title_too_long'
    | 'link_unsupported'
    | 'video_unsupported'
    | 'empty_content'
    | 'account_missing'
    | 'not_supported';
  message: string;
  field?: 'content' | 'media' | 'title' | 'hashtags' | 'account' | 'meta';
}

/** One entry in the calendar view. */
export interface CalendarItem {
  platformPostId: string;
  postId: string;
  platform: PlatformId;
  title: string | null;
  excerpt: string;
  status: PlatformPostStatus;
  scheduledAt: number | null;
  publishedAt: number | null;
  externalUrl: string | null;
  simulated: boolean;
  accountName: string | null;
}

/** Outcome of a single publish attempt (used by publish endpoints). */
export interface PublishAttempt {
  platformPostId: string;
  platform: PlatformId;
  ok: boolean;
  externalId: string | null;
  externalUrl: string | null;
  error: string | null;
  simulated: boolean;
}

/** Idea returned by POST /api/ai/ideas. */
export interface ContentIdea {
  title: string;
  angle: string;
  hook: string;
  suggestedPlatforms: PlatformId[];
}

/** Cursor pagination envelope. */
export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
}

/* ------------------------------------------------------------------ */
/* Type guards                                                         */
/* ------------------------------------------------------------------ */

export function isPlatformId(value: unknown): value is PlatformId {
  return typeof value === 'string' && (PLATFORM_IDS as readonly string[]).includes(value);
}

export function isWorkspaceRole(value: unknown): value is WorkspaceRole {
  return typeof value === 'string' && (WORKSPACE_ROLES as readonly string[]).includes(value);
}

export function isPostStatus(value: unknown): value is PostStatus {
  return typeof value === 'string' && (POST_STATUSES as readonly string[]).includes(value);
}

export function isPlatformPostStatus(value: unknown): value is PlatformPostStatus {
  return (
    typeof value === 'string' && (PLATFORM_POST_STATUSES as readonly string[]).includes(value)
  );
}

/** owner > admin > member */
export function roleAtLeast(role: WorkspaceRole, minimum: WorkspaceRole): boolean {
  const rank: Record<WorkspaceRole, number> = { owner: 3, admin: 2, member: 1 };
  return rank[role] >= rank[minimum];
}
